import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Award, Calendar, GitBranch, Building2, Sparkles, MapPin, IdCard, Trophy, Quote, Package, Pencil, ExternalLink, TrendingUp, ArrowUpRight, Video, Users, Heart, ChevronRight, ChevronLeft, RefreshCw, Wand2, Inbox, IndianRupee, Send, UserPlus, BookOpen, GraduationCap, UserPlus2, Bookmark, ArrowRight, Briefcase, Mic, Clock, FileText, Map as MapIcon } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const mapPreviewIcon = L.divIcon({
  className: "wowshe-preview-pin",
  html: `<div style="width:28px;height:28px;border-radius:50%;background:#FFF8F0;border:2px solid #FFF8F0;box-shadow:0 4px 10px rgba(58,15,77,.35),0 0 0 2px rgba(157,43,107,.6);overflow:hidden"><img src="/wowshe-mark.png" alt="" style="width:100%;height:100%;object-fit:cover" /></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -14],
});
import DashboardShell, { ColorStat } from "@/components/DashboardShell";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { EditProfileModal } from "@/pages/MemberProfile";

export default function Dashboard() {
  const { user, updateProfile, upgrade } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "overview";
  const setTab = (t) => setSp(t === "overview" ? {} : { tab: t });

  const [myEvents, setMyEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [aiMatches, setAiMatches] = useState(null); // null=loading, []=loaded
  const [aiRefreshing, setAiRefreshing] = useState(false);
  const [courses, setCourses] = useState([]);
  const [docs, setDocs] = useState([]);
  const [edit, setEdit] = useState({ ...user });
  const [busy, setBusy] = useState(false);
  const [richOpen, setRichOpen] = useState(false);

  useEffect(() => {
    setEdit({ ...user });
  }, [user]);
  useEffect(() => {
    api.get("/events/my").then(r => setMyEvents(r.data)).catch(() => {});
    api.get("/events").then(r => setAllEvents(r.data)).catch(() => {});
    api.get("/referrals/my").then(r => setReferrals(r.data)).catch(() => {});
    api.get("/members?limit=12").then(r => setRecommendations(r.data.filter(m => m.id !== user?.id).slice(0, 4))).catch(() => {});
    api.get("/ai/match-suggestions").then(r => setAiMatches(r.data.matches || [])).catch(() => setAiMatches([]));
    api.get("/courses").then(r => setCourses(r.data || [])).catch(() => {});
    api.get("/documents").then(r => setDocs(r.data || [])).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const refreshAiMatches = async () => {
    setAiRefreshing(true);
    try {
      const r = await api.get("/ai/match-suggestions?refresh=true");
      setAiMatches(r.data.matches || []);
      toast.success("New AI matches generated");
    } catch { toast.error("Could not refresh"); }
    finally { setAiRefreshing(false); }
  };

  const reserveSpot = async (eventId) => {
    try {
      await api.post("/events/register", { event_id: eventId });
      toast.success("Reserved · See you there!");
      const r = await api.get("/events/my");
      setMyEvents(r.data);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Could not reserve");
    }
  };

  const myEventIds = new Set(myEvents.map((e) => e.id));

  if (!user) return null;
  const isAnnual = user.membership_status === "annual";

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await updateProfile({ name: edit.name, phone: edit.phone, city: edit.city, industry: edit.industry, business: edit.business, bio: edit.bio, avatar: edit.avatar, chapter: edit.chapter, linkedin: edit.linkedin, instagram: edit.instagram, website: edit.website });
      toast.success("Profile updated.");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); } finally { setBusy(false); }
  };

  const handleUpgrade = async () => { try { const r = await upgrade(); toast.success(r.message); } catch { toast.error("Upgrade failed"); } };

  const actions = !isAnnual ? (
    <button onClick={handleUpgrade} data-testid="dashboard-upgrade" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-rose to-champagne text-aubergine text-xs uppercase tracking-widest font-semibold hover:-translate-y-0.5 transition-all">
      <Sparkles className="w-3.5 h-3.5" /> Upgrade to Annual
    </button>
  ) : (
    <Link to="/referrals" data-testid="dashboard-referrals-link" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-aubergine text-ivory text-xs uppercase tracking-widest hover:bg-aubergineLight">
      <GitBranch className="w-3.5 h-3.5" /> New Referral
    </Link>
  );

  return (
    <DashboardShell role="member" title={`Welcome, ${user.name?.split(" ")[0] || "Member"}`} subtitle="Your WOWSHE Dashboard" actions={actions}>
      {tab === "overview" && (
        <OverviewSection
          user={user}
          isAnnual={isAnnual}
          myEvents={myEvents}
          referrals={referrals}
          recommendations={recommendations}
          aiMatches={aiMatches}
          aiRefreshing={aiRefreshing}
          onRefreshAi={refreshAiMatches}
          onUpgrade={handleUpgrade}
          courses={courses}
          docs={docs}
        />
      )}

      {tab === "profile" && (
        <div className="space-y-6 max-w-4xl">
          <form onSubmit={saveProfile} className="bg-white rounded-2xl border border-aubergine/8 p-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            <h2 className="font-display text-2xl text-aubergine md:col-span-2 flex items-center gap-2"><IdCard className="w-5 h-5 text-rose" /> Your profile</h2>
            {[
              ["Full name", "name"], ["Phone", "phone"], ["City", "city"], ["Industry", "industry"],
              ["Business / Role", "business"], ["Chapter", "chapter"], ["LinkedIn", "linkedin"], ["Instagram", "instagram"],
              ["Website", "website"], ["Avatar URL", "avatar"],
            ].map(([label, key]) => (
              <div key={key}>
                <label className="label-luxe">{label}</label>
                <input data-testid={`prof-${key}`} value={edit[key] || ""} onChange={(e) => setEdit({ ...edit, [key]: e.target.value })} className="input-luxe" />
              </div>
            ))}
            <div className="md:col-span-2">
              <label className="label-luxe">Bio</label>
              <textarea data-testid="prof-bio" rows={4} value={edit.bio || ""} onChange={(e) => setEdit({ ...edit, bio: e.target.value })} className="input-luxe" />
            </div>
            <button data-testid="prof-save" disabled={busy} className="btn-primary md:col-span-2">{busy ? "Saving…" : "Save profile"}</button>
          </form>

          {/* Rich showcase editor */}
          <div className="bg-gradient-to-br from-lavender/60 to-white rounded-2xl border border-aubergine/10 p-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <p className="overline">Showcase & catalogue</p>
                <h3 className="font-display text-2xl text-aubergine mt-2">Tell your full story</h3>
                <p className="text-sm text-plumSoft mt-2 max-w-xl">Add achievements, peer testimonials, your products & services, and contact channels. These appear on your public Member Profile to help others discover, refer and collaborate with you.</p>
              </div>
              <div className="flex flex-col gap-2.5 shrink-0">
                <button onClick={() => setRichOpen(true)} data-testid="open-rich-editor" className="btn-primary inline-flex items-center gap-2"><Pencil className="w-3.5 h-3.5" /> Open rich editor</button>
                <Link to={`/members/${user.id}`} data-testid="view-my-profile" className="btn-ghost text-xs inline-flex items-center gap-2 justify-center"><ExternalLink className="w-3.5 h-3.5" /> View my profile</Link>
              </div>
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3">
              <Tile icon={Trophy} label="Achievements" count={user.achievements?.length || 0} tone="text-rose" />
              <Tile icon={Quote} label="Testimonials" count={user.testimonials?.length || 0} tone="text-plum" />
              <Tile icon={Package} label="Products & services" count={user.products?.length || 0} tone="text-teal-600" />
            </div>
          </div>

          {richOpen && (
            <EditProfileModal
              initial={user}
              onClose={() => setRichOpen(false)}
              onSave={async (payload) => {
                try {
                  await updateProfile(payload);
                  toast.success("Profile updated.");
                  setRichOpen(false);
                } catch (err) {
                  toast.error(formatErr(err.response?.data?.detail) || "Failed to save");
                }
              }}
            />
          )}
        </div>
      )}

      {tab === "events" && (
        <div data-testid="dash-events-tab">
          <div className="flex items-center justify-between mb-5">
            <div>
              <p className="text-[11px] uppercase tracking-[0.14em] text-plumSoft/70">Events</p>
              <h2 className="text-aubergine text-xl mt-1" style={{ fontFamily: '"Outfit", sans-serif' }}>
                Upcoming gatherings · {allEvents.filter(e => !e.is_past).length} live
              </h2>
            </div>
            {myEvents.length > 0 && (
              <p className="text-xs text-plum">You're reserved for <span className="font-medium text-aubergine">{myEvents.length}</span></p>
            )}
          </div>

          {allEvents.length === 0 ? (
            <div className="bg-white rounded-2xl border border-aubergine/[0.06] p-12 text-center">
              <Calendar className="w-10 h-10 mx-auto text-plumSoft/60" />
              <p className="text-aubergine mt-4" style={{ fontFamily: '"Outfit", sans-serif' }}>No events scheduled</p>
              <p className="text-sm text-plumSoft mt-2">Check back soon — chapter events are added weekly.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {allEvents.map((ev) => {
                const isReserved = myEventIds.has(ev.id);
                const isPast = ev.is_past;
                return (
                  <article key={ev.id} data-testid={`event-card-${ev.id}`} className="bg-white rounded-2xl border border-aubergine/[0.06] overflow-hidden flex flex-col hover:border-aubergine/15 transition-colors">
                    <div className="aspect-[4/3] overflow-hidden bg-lavender/40 relative">
                      <img src={ev.image} alt="" className="w-full h-full object-cover" />
                      {isPast && <div className="absolute top-3 left-3 bg-aubergine/90 text-ivory text-[10px] px-2.5 py-1 rounded-full uppercase tracking-widest">Past</div>}
                      {!isPast && isReserved && <div className="absolute top-3 left-3 bg-emerald-600 text-ivory text-[10px] px-2.5 py-1 rounded-full uppercase tracking-widest">Reserved</div>}
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <span className="text-[10px] uppercase tracking-[0.14em] text-rose">{ev.type}</span>
                      <h3 className="text-aubergine mt-1.5 leading-snug" style={{ fontFamily: '"Outfit", sans-serif', fontSize: "16px" }}>{ev.title}</h3>
                      <div className="mt-3 text-xs text-plumSoft space-y-1.5 flex-1">
                        <div className="flex items-center gap-2"><Calendar className="w-3 h-3" /> {new Date(ev.date).toLocaleString("en-IN", { dateStyle: "medium" })}</div>
                        <div className="flex items-center gap-2"><MapPin className="w-3 h-3" /> {ev.location}</div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-aubergine/[0.06]">
                        {isPast ? (
                          <p className="text-xs text-plumSoft/70">This event has ended.</p>
                        ) : isReserved ? (
                          <button disabled className="w-full text-xs uppercase tracking-widest px-3.5 py-2.5 rounded-full bg-emerald-50 text-emerald-700 font-medium">
                            ✓ Reserved
                          </button>
                        ) : (
                          <button
                            data-testid={`reserve-${ev.id}`}
                            onClick={() => reserveSpot(ev.id)}
                            className="w-full text-xs uppercase tracking-widest px-3.5 py-2.5 rounded-full bg-aubergine text-ivory hover:bg-aubergineLight transition-colors"
                          >
                            Reserve your spot
                          </button>
                        )}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      )}
    </DashboardShell>
  );
}

function Tile({ icon: Icon, label, count, tone }) {
  return (
    <div className="bg-white rounded-xl border border-aubergine/8 p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg bg-lavender/60 flex items-center justify-center ${tone}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-plumSoft uppercase tracking-widest">{label}</p>
        <p className="font-display text-aubergine text-xl mt-0.5">{count}</p>
      </div>
    </div>
  );
}

// ---------- New Overview Section (Coursue-style) ----------
function OverviewSection({ user, isAnnual, myEvents, referrals, recommendations, aiMatches, aiRefreshing, onRefreshAi, onUpgrade, courses = [], docs = [] }) {
  const myId = user.id;
  const referralsReceived = referrals.filter(r => r.to_member_id === myId);
  const referralsGiven = referrals.filter(r => r.from_user_id === myId);
  const businessConverted = referrals.filter(r => (r.status === "closed_won" || r.status === "accepted") && (r.to_member_id === myId || r.from_user_id === myId)).reduce((s, r) => s + (Number(r.value) || 0), 0);
  const sortedEvents = [...myEvents].sort((a, b) => new Date(a.date) - new Date(b.date));

  // Build unified feed: events first, then lessons — for the carousel
  const feed = [
    ...sortedEvents.slice(0, 4).map((e) => ({
      kind: "event",
      id: `e-${e.id}`,
      raw: e,
      to: "/events",
    })),
    ...courses.slice(0, 4).map((c) => ({
      kind: "lesson",
      id: `c-${c.id}`,
      raw: c,
      to: "/learning",
    })),
  ];

  const [carouselIdx, setCarouselIdx] = useState(0);
  const visibleCount = 3;
  const maxIdx = Math.max(0, feed.length - visibleCount);
  const carousel = feed.slice(carouselIdx, carouselIdx + visibleCount);

  // Greeting based on time
  const hr = new Date().getHours();
  const greeting = hr < 12 ? "Good Morning" : hr < 17 ? "Good Afternoon" : "Good Evening";
  const firstName = user.name?.split(" ")[0] || "Member";

  // Daily-rotating quote
  const todaysQuote = (() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 0);
    const dayOfYear = Math.floor((now - start) / 86400000);
    return DAILY_QUOTES[dayOfYear % DAILY_QUOTES.length];
  })();

  // Profile completion %
  const profileFields = [
    user.name, user.business, user.industry, user.city, user.chapter, user.bio, user.avatar,
    user.phone, user.linkedin, user.website,
    (user.achievements || []).length > 0 ? "x" : "",
    (user.products || []).length > 0 ? "x" : "",
    (user.testimonials || []).length > 0 ? "x" : "",
  ];
  const filled = profileFields.filter((v) => (v || "").toString().trim().length > 0).length;
  const completion = Math.round((filled / profileFields.length) * 100);

  // Category cards data
  const businessGainedFmt = businessConverted > 0
    ? (businessConverted >= 100000 ? `₹${(businessConverted / 100000).toFixed(1)}L` : `₹${(businessConverted / 1000).toFixed(0)}K`)
    : "₹0";
  const cats = [
    { key: "business", icon: IndianRupee, label: "Business Gained", value: businessGainedFmt, tint: "bg-rose/12 text-rose", valueClass: "text-rose" },
    { key: "given", icon: Send, label: "Referrals Given", value: referralsGiven.length, tint: "bg-champagne/40 text-amber-600", valueClass: "text-amber-600" },
    { key: "received", icon: Inbox, label: "Referrals Received", value: referralsReceived.length, tint: "bg-lavender text-aubergine", valueClass: "text-aubergine" },
  ];

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
      {/* LEFT + CENTER (8 cols) */}
      <div className="xl:col-span-8 flex flex-col gap-5">
        {/* HERO — Daily motivational quote */}
        <div data-testid="hero-quote" className="relative rounded-3xl overflow-hidden p-8 md:p-10 bg-gradient-to-br from-aubergine via-aubergine to-plum text-ivory min-h-[260px] flex flex-col justify-between">
          {/* Sparkle decoration */}
          <svg className="absolute right-6 top-6 w-44 h-44 opacity-50 pointer-events-none" viewBox="0 0 200 200" fill="none">
            <path d="M100 20 L108 92 L180 100 L108 108 L100 180 L92 108 L20 100 L92 92 Z" fill="rgba(255,255,255,0.25)" />
            <path d="M160 40 L164 64 L188 68 L164 72 L160 96 L156 72 L132 68 L156 64 Z" fill="rgba(255,255,255,0.15)" />
            <path d="M50 130 L54 152 L76 156 L54 160 L50 182 L46 160 L24 156 L46 152 Z" fill="rgba(255,255,255,0.18)" />
          </svg>
          <div className="absolute inset-0 grain opacity-20 pointer-events-none" />

          <div className="relative">
            <p className="text-[10px] uppercase tracking-[0.22em] text-champagne flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> Quote of the Day
            </p>
            <Quote className="w-7 h-7 text-champagne/60 mt-5" />
            <blockquote className="mt-2 text-xl md:text-2xl lg:text-[26px] leading-snug max-w-3xl font-light" style={{ fontFamily: '"Playfair Display", serif' }}>
              &ldquo;{todaysQuote.q}&rdquo;
            </blockquote>
          </div>

          <div className="relative mt-6 flex items-end justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-8 h-[1px] bg-champagne/60" />
              <div>
                <p className="text-sm text-ivory font-medium" style={{ fontFamily: '"Outfit", sans-serif' }}>{todaysQuote.a}</p>
                {todaysQuote.r && <p className="text-[10px] uppercase tracking-widest text-champagne/80 mt-0.5">{todaysQuote.r}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Category stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {cats.map((c) => {
            const Icon = c.icon;
            return (
              <div key={c.key} data-testid={`cat-${c.key}`} className="bg-white rounded-2xl border border-aubergine/[0.06] p-4 flex items-center gap-3.5 hover:border-aubergine/15 hover:shadow-luxe transition-all">
                <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${c.tint}`}>
                  <Icon className="w-[18px] h-[18px]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-2xl font-semibold leading-none tracking-tight ${c.valueClass}`} style={{ fontFamily: '"Outfit", sans-serif' }}>
                    {c.value}
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-plumSoft/70 mt-1.5 truncate">{c.label}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Continue Watching / Lessons carousel */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-aubergine text-xl" style={{ fontFamily: '"Outfit", sans-serif' }}>Events & Learnings</h3>
            <div className="flex items-center gap-2">
              <button onClick={() => setCarouselIdx(Math.max(0, carouselIdx - 1))} disabled={carouselIdx === 0} className="w-9 h-9 rounded-full bg-white border border-aubergine/[0.08] text-aubergine hover:border-aubergine/30 disabled:opacity-40 flex items-center justify-center"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setCarouselIdx(Math.min(maxIdx, carouselIdx + 1))} disabled={carouselIdx >= maxIdx} className="w-9 h-9 rounded-full bg-aubergine text-ivory hover:bg-aubergineLight disabled:opacity-40 flex items-center justify-center"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
          {carousel.length === 0 ? (
            <div className="bg-white rounded-2xl border border-aubergine/[0.06] p-10 text-center">
              <Calendar className="w-7 h-7 mx-auto text-plumSoft/40 mb-2" />
              <p className="text-sm text-plumSoft/80">No upcoming sessions yet. <Link to="/events" className="text-rose hover:underline">Browse events →</Link> or <Link to="/learning" className="text-rose hover:underline">explore lessons →</Link></p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {carousel.map((item) => item.kind === "event" ? (
                <DashEventCard key={item.id} ev={item.raw} to={item.to} testid={`feed-${item.id}`} />
              ) : (
                <DashLessonCard key={item.id} c={item.raw} to={item.to} testid={`feed-${item.id}`} />
              ))}
            </div>
          )}
        </div>

        {/* Testimonials · Invoices · Quotations — landscape rows */}
        {(() => {
          const testimonials = user.testimonials || [];
          const invoices = docs.filter((d) => d.doc_type === "invoice");
          const quotations = docs.filter((d) => d.doc_type === "quotation");
          const invoiceTotal = invoices.reduce((s, d) => s + Number(d.totals?.grand_total || 0), 0);
          const quotationTotal = quotations.reduce((s, d) => s + Number(d.totals?.grand_total || 0), 0);
          const paidCount = invoices.filter((d) => d.status === "paid").length;
          const acceptedCount = quotations.filter((d) => d.status === "accepted").length;
          const fmt = (n) => n >= 100000 ? `₹${(n / 100000).toFixed(1)}L` : `₹${(n / 1000).toFixed(0)}K`;

          const cards = [
            { key: "testimonials", overline: "Praise", title: "Testimonials", icon: Quote, accent: "from-rose to-champagne", iconBg: "bg-rose/12 text-rose", overlineColor: "text-rose", count: testimonials.length, sub: "received", to: "/dashboard?tab=profile", cta: "Manage testimonials" },
          ];

          return (
            <div className="space-y-4">
              {cards.map((c) => {
                const Icon = c.icon;
                return (
                  <div key={c.key} data-testid={`${c.key}-card`} className="relative bg-white rounded-2xl border border-aubergine/[0.06] overflow-hidden flex">                    {/* Accent strip — left edge for landscape */}
                    <div className={`w-1.5 bg-gradient-to-b ${c.accent} shrink-0`} />
                    <div className="flex-1 p-4 flex items-center gap-5 min-w-0">
                      {/* Identity block */}
                      <div className="flex items-center gap-3 shrink-0 w-48">
                        <span className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${c.iconBg}`}><Icon className="w-4 h-4" /></span>
                        <div className="min-w-0">
                          <p className={`text-[9px] uppercase tracking-[0.16em] font-medium ${c.overlineColor}`}>{c.overline}</p>
                          <h3 className="text-aubergine text-[15px] mt-0.5 truncate" style={{ fontFamily: '"Outfit", sans-serif' }}>{c.title}</h3>
                        </div>
                      </div>

                      {/* Numbers block */}
                      <div className="flex items-center gap-5 shrink-0">
                        <div>
                          <p className="text-2xl font-semibold text-aubergine leading-none" style={{ fontFamily: '"Outfit", sans-serif' }}>{c.count}</p>
                          <p className="text-[9px] uppercase tracking-widest text-plumSoft/70 mt-1">{c.sub}</p>
                        </div>
                        {c.value && (
                          <div className="pl-5 border-l border-aubergine/[0.08]">
                            <p className="text-lg font-semibold text-aubergine leading-none" style={{ fontFamily: '"Outfit", sans-serif' }}>{c.value}</p>
                            <p className="text-[9px] uppercase tracking-widest text-emerald-600 mt-1">{c.valueLabel}</p>
                          </div>
                        )}
                      </div>

                      {/* Preview block — single latest item */}
                      <div className="flex-1 min-w-0 hidden md:block">
                        {c.key === "testimonials" && (
                          testimonials.length === 0 ? (
                            <p className="text-xs text-plumSoft/70">No testimonials yet — <Link to="/dashboard?tab=profile" className="text-rose hover:underline">add one →</Link></p>
                          ) : (
                            <div data-testid="testimonial-0" className="p-3 rounded-xl bg-gradient-to-br from-rose/[0.04] to-champagne/[0.04] border border-rose/10">
                              <p className="text-[12px] text-aubergine font-light italic line-clamp-2 leading-snug">&ldquo;{testimonials[0].quote}&rdquo;</p>
                              <div className="flex items-center gap-2 mt-2">
                                {testimonials[0].avatar ? <img src={testimonials[0].avatar} alt={testimonials[0].author} className="w-5 h-5 rounded-full object-cover" /> : <div className="w-5 h-5 rounded-full bg-aubergine/[0.08] text-aubergine flex items-center justify-center text-[9px]">{testimonials[0].author?.[0]}</div>}
                                <p className="text-[10px] text-aubergine font-medium truncate">{testimonials[0].author}</p>
                                {testimonials.length > 1 && <span className="text-[9px] text-plumSoft/60">+{testimonials.length - 1} more</span>}
                              </div>
                            </div>
                          )
                        )}
                        {c.key === "invoices" && (
                          invoices.length === 0 ? <p className="text-xs text-plumSoft/70">No invoices yet.</p> : (
                            <div className="flex items-center gap-3 p-3 rounded-xl bg-champagne/[0.06] border border-champagne/30" data-testid="invoices-list">
                              <div className="min-w-0 flex-1">
                                <p className="text-[10px] uppercase tracking-widest text-plumSoft/60">Latest</p>
                                <p className="text-[13px] text-aubergine font-medium truncate">{invoices[0].number} · {invoices[0].recipient?.name || ""}</p>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-[14px] text-aubergine font-semibold">₹{Number(invoices[0].totals?.grand_total || 0).toLocaleString("en-IN")}</p>
                                <p className={`text-[9px] uppercase tracking-widest ${invoices[0].status === "paid" ? "text-emerald-600" : invoices[0].status === "sent" ? "text-amber-600" : "text-plumSoft/70"}`}>{invoices[0].status || "draft"}</p>
                              </div>
                              {invoices.length > 1 && <span className="text-[9px] text-plumSoft/60 shrink-0">+{invoices.length - 1} more</span>}
                            </div>
                          )
                        )}
                        {c.key === "quotations" && (
                          quotations.length === 0 ? <p className="text-xs text-plumSoft/70">No quotations yet.</p> : (
                            <div className="flex items-center gap-3 p-3 rounded-xl bg-lavender/40 border border-aubergine/[0.08]" data-testid="quotations-list">
                              <div className="min-w-0 flex-1">
                                <p className="text-[10px] uppercase tracking-widest text-plumSoft/60">Latest</p>
                                <p className="text-[13px] text-aubergine font-medium truncate">{quotations[0].number} · {quotations[0].recipient?.name || ""}</p>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-[14px] text-aubergine font-semibold">₹{Number(quotations[0].totals?.grand_total || 0).toLocaleString("en-IN")}</p>
                                <p className={`text-[9px] uppercase tracking-widest ${quotations[0].status === "accepted" ? "text-emerald-600" : quotations[0].status === "sent" ? "text-amber-600" : "text-plumSoft/70"}`}>{quotations[0].status || "draft"}</p>
                              </div>
                              {quotations.length > 1 && <span className="text-[9px] text-plumSoft/60 shrink-0">+{quotations.length - 1} more</span>}
                            </div>
                          )
                        )}
                      </div>

                      {/* CTA */}
                      <Link to={c.to} className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-lavender/40 hover:bg-aubergine hover:text-ivory text-aubergine text-[10px] uppercase tracking-widest transition-colors">
                        {c.cta} <ChevronRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Members Map Preview */}
        <MembersMapPreview chapter={user.chapter} />
      </div>

      {/* RIGHT (4 cols) — Statistic + Mentors */}
      <aside className="xl:col-span-4 space-y-5">
        {/* Statistic */}
        <div className="bg-white rounded-3xl border border-aubergine/[0.06] p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-aubergine text-lg" style={{ fontFamily: '"Outfit", sans-serif' }}>Statistic</h3>
            <button className="text-plumSoft/50 hover:text-aubergine">⋮</button>
          </div>

          {/* Profile completion ring */}
          <div className="mt-6 flex justify-center">
            <div className="relative w-44 h-44">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="46" stroke="rgba(44,14,39,0.08)" strokeWidth="3" fill="none" />
                <circle
                  cx="50" cy="50" r="46"
                  stroke="url(#ringGrad)" strokeWidth="3" fill="none"
                  strokeDasharray={`${(completion / 100) * 2 * Math.PI * 46} ${2 * Math.PI * 46}`}
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient id="ringGrad" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#7A1F66" />
                    <stop offset="100%" stopColor="#D85F8C" />
                  </linearGradient>
                </defs>
              </svg>
              {/* Avatar */}
              <div className="absolute inset-3 rounded-full overflow-hidden bg-lavender">
                {user.avatar ? (
                  <img src={user.avatar} alt={user.name} className="w-full h-full object-cover object-center" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-3xl text-aubergine font-medium" style={{ fontFamily: '"Outfit", sans-serif' }}>{firstName[0]}</div>
                )}
              </div>
              {/* % badge */}
              <div className="absolute -top-1 right-2 bg-aubergine text-ivory text-[11px] font-medium px-2.5 py-1 rounded-full" data-testid="profile-completion">
                {completion}%
              </div>
            </div>
          </div>

          <div className="mt-5 text-center">
            <h4 className="text-aubergine text-lg flex items-center justify-center gap-1.5" style={{ fontFamily: '"Outfit", sans-serif' }}>
              {greeting} {firstName} <span className="text-rose">✦</span>
            </h4>
            <p className="text-xs text-plumSoft/70 mt-1">Complete your profile to attract better referrals.</p>
          </div>

          {/* Goals · This Quarter */}
          <div className="mt-6 pt-5 border-t border-aubergine/[0.06]" data-testid="goals-section">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[10px] uppercase tracking-[0.14em] text-plumSoft/60">Goals · This Quarter</p>
              <Trophy className="w-3.5 h-3.5 text-rose" />
            </div>
            {(() => {
              const visitorsByMe = Math.max(0, Math.round(referralsGiven.length * 1.4));
              const goals = [
                { label: "Referrals Given", current: referralsGiven.length, target: 10, color: "from-aubergine to-rose", iconBg: "bg-rose/12 text-rose", icon: Send },
                { label: "Visitors Brought", current: visitorsByMe, target: 6, color: "from-rose to-champagne", iconBg: "bg-champagne/40 text-amber-600", icon: UserPlus },
                { label: "Pitches Posted", current: user.asks_history?.length || 0, target: 12, color: "from-amber-500 to-champagne", iconBg: "bg-champagne/40 text-amber-600", icon: Mic },
                { label: "Attendance", current: myEvents.length, target: 8, color: "from-aubergine to-plum", iconBg: "bg-lavender text-aubergine", icon: Calendar },
              ];
              return (
                <ul className="space-y-4">
                  {goals.map((g, i) => {
                    const pct = Math.min(100, Math.round((g.current / g.target) * 100));
                    const done = pct >= 100;
                    const display = `${g.current} / ${g.target}`;
                    const Icon = g.icon;
                    return (
                      <li key={g.label} data-testid={`goal-${i}`}>
                        <div className="flex items-center justify-between gap-3 mb-1.5">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${g.iconBg}`}><Icon className="w-3 h-3" /></span>
                            <span className="text-[12px] text-aubergine font-medium truncate" style={{ fontFamily: '"Outfit", sans-serif' }}>{g.label}</span>
                          </div>
                          <span className={`text-[11px] font-medium shrink-0 ${done ? "text-emerald-600" : "text-plumSoft"}`}>{display}{done && " ✓"}</span>
                        </div>
                        <div className="h-2 rounded-full bg-aubergine/[0.06] overflow-hidden">
                          <div className={`h-full bg-gradient-to-r ${done ? "from-emerald-500 to-emerald-400" : g.color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              );
            })()}
          </div>
        </div>

        {/* Chapter Leaderboard */}
        <div className="bg-white rounded-3xl border border-aubergine/[0.06] p-6" data-testid="chapter-leaderboard">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.14em] text-plumSoft/60">{user.chapter || "Your Chapter"}</p>
              <h3 className="text-aubergine text-lg mt-0.5" style={{ fontFamily: '"Outfit", sans-serif' }}>Chapter Leaderboard</h3>
            </div>
            <span className="w-8 h-8 rounded-full bg-champagne/30 text-amber-600 flex items-center justify-center">
              <Trophy className="w-3.5 h-3.5" />
            </span>
          </div>

          {(() => {
            // Build leaderboard: combine current user + directory recommendations, sort by deterministic Network Score
            const pool = [user, ...recommendations];
            const scored = pool.map((m) => {
              const ach = (m.achievements || []).length;
              const prods = (m.products || []).length;
              const asks = (m.asks_history || []).length;
              const hash = (m.id || m.name || "").split("").reduce((s, c) => s + c.charCodeAt(0), 0);
              const score = ach * 8 + prods * 6 + asks * 12 + (hash % 30);
              return { ...m, score };
            }).sort((a, b) => b.score - a.score).slice(0, 5);

            if (scored.length === 0) {
              return <p className="text-sm text-plumSoft/80 text-center py-4">Loading…</p>;
            }

            const medals = ["bg-amber-400 text-white", "bg-plumSoft/30 text-aubergine", "bg-champagne/60 text-amber-800"];
            return (
              <ul className="space-y-3">
                {scored.map((m, i) => {
                  const isMe = m.id === user.id;
                  return (
                    <li key={m.id} data-testid={`leader-${i + 1}`} className={`flex items-center gap-3 p-2 -mx-2 rounded-lg ${isMe ? "bg-rose/5" : ""}`}>
                      {/* Rank badge */}
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold shrink-0 ${i < 3 ? medals[i] : "bg-aubergine/[0.05] text-plumSoft"}`} style={{ fontFamily: '"Outfit", sans-serif' }}>
                        {i + 1}
                      </div>
                      {/* Avatar */}
                      {m.avatar ? (
                        <img src={m.avatar} alt={m.name} className="w-9 h-9 rounded-full object-cover object-center" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-aubergine/[0.08] text-aubergine flex items-center justify-center text-sm font-medium">{m.name?.[0] || "?"}</div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-aubergine font-medium text-sm truncate flex items-center gap-1.5" style={{ fontFamily: '"Outfit", sans-serif' }}>
                          {m.name} {isMe && <span className="text-[9px] uppercase tracking-widest text-rose px-1.5 py-0.5 rounded-full bg-rose/10">You</span>}
                        </p>
                        <p className="text-[11px] text-plumSoft/70 truncate">{m.business || m.industry || "Member"}</p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-semibold text-aubergine" style={{ fontFamily: '"Outfit", sans-serif' }}>{m.score}</p>
                        <p className="text-[9px] uppercase tracking-widest text-plumSoft/60">pts</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            );
          })()}

          <Link to="/chapters" className="mt-5 block text-center text-xs text-aubergine bg-lavender/50 hover:bg-lavender py-2.5 rounded-full transition-colors">View Full Leaderboard</Link>
        </div>

        {/* 1-on-1 Suggestions */}
        <div className="bg-white rounded-3xl border border-aubergine/[0.06] p-6" data-testid="one-on-one-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-aubergine text-lg" style={{ fontFamily: '"Outfit", sans-serif' }}>1-on-1 Suggestions</h3>
            <span className="w-8 h-8 rounded-full bg-rose/10 text-rose flex items-center justify-center">
              <Briefcase className="w-3.5 h-3.5" />
            </span>
          </div>

          {recommendations.length === 0 ? (
            <p className="text-sm text-plumSoft/80 text-center py-4">Loading suggestions…</p>
          ) : (
            <ul className="space-y-3.5">
              {recommendations.slice(0, 3).map((m, i) => {
                const slots = ["Thu · 11 AM coffee", "Fri · 4 PM tea", "Sat · 10 AM walk"];
                const slot = slots[i % slots.length];
                return (
                  <li key={m.id} data-testid={`one-on-one-${i}`} className={`flex items-center gap-3 ${i < Math.min(recommendations.length, 3) - 1 ? "pb-3.5 border-b border-aubergine/[0.04]" : ""}`}>
                    {m.avatar ? (
                      <img src={m.avatar} alt={m.name} className="w-10 h-10 rounded-full object-cover object-center" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-aubergine/[0.08] text-aubergine flex items-center justify-center font-medium">{m.name?.[0] || "?"}</div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-aubergine font-medium text-sm truncate" style={{ fontFamily: '"Outfit", sans-serif' }}>{m.name}</p>
                      <p className="text-[11px] text-plumSoft/70 truncate">{m.business || m.industry || "Member"}</p>
                      <p className="text-[10px] text-rose mt-0.5 flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{slot}</p>
                    </div>
                    <Link to={`/members/${m.id}`} className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-aubergine/15 text-aubergine text-[10px] uppercase tracking-widest hover:bg-aubergine hover:text-ivory transition-colors">
                      <UserPlus2 className="w-3 h-3" /> Connect
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}

          <Link to="/directory" className="mt-5 block text-center text-xs text-aubergine bg-lavender/50 hover:bg-lavender py-2.5 rounded-full transition-colors">Browse Directory</Link>
        </div>
      </aside>
    </div>
  );
}

function KpiCard({ label, value, sub, filled, icon: Icon = ArrowUpRight }) {
  if (filled) {
    return (
      <div className="rounded-2xl p-5 bg-gradient-to-br from-aubergine via-aubergine to-plum text-ivory relative overflow-hidden group">
        <div className="absolute inset-0 grain opacity-15" />
        <div className="relative">
          <div className="flex items-start justify-between">
            <p className="text-[11px] uppercase tracking-[0.14em] text-ivory/70">{label}</p>
            <div className="w-9 h-9 rounded-xl bg-ivory/15 flex items-center justify-center group-hover:bg-ivory/25 transition-colors">
              <Icon className="w-4 h-4" />
            </div>
          </div>
          <p className="text-4xl mt-5 font-medium tracking-tight" style={{ fontFamily: '"Outfit", sans-serif' }}>{value}</p>
          <p className="text-[11px] text-ivory/70 mt-3 flex items-center gap-1.5"><TrendingUp className="w-3 h-3" /> {sub}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-2xl p-5 bg-white border border-aubergine/[0.06] hover:border-aubergine/15 transition-colors">
      <div className="flex items-start justify-between">
        <p className="text-[11px] uppercase tracking-[0.14em] text-plumSoft/70">{label}</p>
        <div className="w-9 h-9 rounded-xl bg-aubergine/[0.05] flex items-center justify-center text-aubergine">
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <p className="text-4xl mt-5 font-medium text-aubergine tracking-tight" style={{ fontFamily: '"Outfit", sans-serif' }}>{value}</p>
      <p className="text-[11px] text-plumSoft/70 mt-3 flex items-center gap-1.5"><TrendingUp className="w-3 h-3 text-emerald-500" /> {sub}</p>
    </div>
  );
}

function EventDateBadge({ dateString }) {
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return null;
  const day = d.getDate();
  const month = d.toLocaleString("en-IN", { month: "short" }).toUpperCase();
  return (
    <div className="w-11 h-11 rounded-xl bg-aubergine/[0.06] flex flex-col items-center justify-center shrink-0">
      <span className="text-[9px] text-plum tracking-widest">{month}</span>
      <span className="text-base font-medium text-aubergine leading-none" style={{ fontFamily: '"Outfit", sans-serif' }}>{day}</span>
    </div>
  );
}

function formatEventDate(dateString) {
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return dateString;
  return d.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });
}

// Members Map preview — interactive Leaflet map showing chapter members' offices
function MembersMapPreview({ chapter }) {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/members-map").then((r) => setPoints(r.data || [])).catch(() => setPoints([])).finally(() => setLoading(false));
  }, []);

  const center = points.length > 0
    ? [points.reduce((s, p) => s + p.office_lat, 0) / points.length, points.reduce((s, p) => s + p.office_lng, 0) / points.length]
    : [22.5, 78.9];

  return (
    <div data-testid="map-preview-card" className="bg-white rounded-2xl border border-aubergine/[0.06] overflow-hidden flex flex-col flex-1 min-h-[420px]">
      <div className="px-5 py-4 flex items-center justify-between gap-3 border-b border-aubergine/[0.06] shrink-0">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="w-9 h-9 rounded-full bg-aubergine/[0.06] text-aubergine flex items-center justify-center shrink-0"><MapIcon className="w-4 h-4" /></span>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.16em] text-aubergine font-medium">Network · {chapter || "All Chapters"}</p>
            <h3 className="text-aubergine text-[15px] mt-0.5" style={{ fontFamily: '"Outfit", sans-serif' }}>Members Map</h3>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-[11px] text-plum">{loading ? "Loading…" : `${points.length} on the map`}</span>
          <Link to="/members-map" className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-lavender/40 hover:bg-aubergine hover:text-ivory text-aubergine text-[10px] uppercase tracking-widest transition-colors">
            Explore map <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
      <div className="flex-1 relative bg-lavender min-h-[300px]">
        {loading ? (
          <div className="h-full flex items-center justify-center text-sm text-plumSoft/70">Loading map…</div>
        ) : (
          <MapContainer center={center} zoom={points.length > 1 ? 4 : 5} scrollWheelZoom={false} className="h-full w-full" style={{ background: "#F3EAF5" }}>
            <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {points.map((p) => (
              <Marker key={p.id} position={[p.office_lat, p.office_lng]} icon={mapPreviewIcon}>
                <Popup>
                  <div className="text-xs">
                    <p className="font-medium text-aubergine">{p.name}</p>
                    {p.business && <p className="text-plumSoft">{p.business}</p>}
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        )}
      </div>
    </div>
  );
}

// Dashboard Event card — mirrors Events page card-luxe style
function DashEventCard({ ev, to, testid }) {
  return (
    <Link to={to} data-testid={testid} className="card-luxe overflow-hidden flex flex-col group">
      <div className="aspect-[4/3] overflow-hidden relative">
        {ev.image ? (
          <img src={ev.image} alt={ev.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-lavender to-aubergine/[0.06] flex items-center justify-center"><Calendar className="w-8 h-8 text-aubergine/30" /></div>
        )}
        <span className="absolute top-4 left-4 bg-ivory/90 backdrop-blur px-3 py-1 text-[10px] tracking-widest uppercase rounded-full text-aubergine">{ev.type || "Event"}</span>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-display text-xl text-aubergine leading-snug line-clamp-2">{ev.title}</h3>
        {ev.description && <p className="text-sm text-plumSoft mt-3 font-light leading-relaxed line-clamp-2">{ev.description}</p>}
        <div className="mt-4 space-y-1.5 text-xs text-plum">
          <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /><span>{new Date(ev.date).toLocaleString("en-IN", { dateStyle: "long", timeStyle: "short" })}</span></div>
          <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /><span className="truncate">{ev.location}</span></div>
          <div className="flex items-center gap-2"><Users className="w-3.5 h-3.5" /><span>{(ev.attendees || []).length} attending</span></div>
        </div>
      </div>
    </Link>
  );
}

// Dashboard Lesson card — mirrors Learning page card-luxe style
function DashLessonCard({ c, to, testid }) {
  return (
    <Link to={to} data-testid={testid} className="card-luxe overflow-hidden group flex flex-col">
      <div className="aspect-[16/10] overflow-hidden">
        {c.image ? (
          <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-lavender to-aubergine/[0.06] flex items-center justify-center"><BookOpen className="w-8 h-8 text-aubergine/30" /></div>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <span className="overline">{c.category}</span>
        <h3 className="font-display text-xl text-aubergine mt-2 leading-snug line-clamp-2">{c.title}</h3>
        {c.description && <p className="text-sm text-plumSoft mt-3 font-light leading-relaxed line-clamp-2">{c.description}</p>}
        <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-plum">
          {c.duration && <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {c.duration}</span>}
          {c.lessons > 0 && <span className="flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> {c.lessons} lessons</span>}
        </div>
        {c.instructor && <p className="mt-3 text-xs text-plumSoft">with {c.instructor}</p>}
        {typeof c.price === "number" && (
          <div className="mt-auto pt-4 border-t border-aubergine/10 flex items-end justify-between">
            <p className="font-display text-xl text-aubergine">₹ {c.price.toLocaleString("en-IN")}</p>
            <span className="flex items-center gap-1 text-sm text-plum">Enroll <ChevronRight className="w-4 h-4" /></span>
          </div>
        )}
      </div>
    </Link>
  );
}

// Daily-rotating curated quotes — business, networking, marketing & leadership wisdom
const DAILY_QUOTES = [
  { q: "Your network is your net worth.", a: "Porter Gale", r: "Author, Marketing Strategist" },
  { q: "Building the right network is as critical as building the right brand.", a: "Sheryl Sandberg", r: "Former COO, Meta" },
  { q: "If you don't believe in your product, no one else will either.", a: "Mary Kay Ash", r: "Founder, Mary Kay Cosmetics" },
  { q: "I never dreamed about success. I worked for it.", a: "Estée Lauder", r: "Founder, Estée Lauder Companies" },
  { q: "Marketing is no longer about the stuff you make but the stories you tell.", a: "Seth Godin", r: "Marketing Author" },
  { q: "Networking is marketing. Marketing yourself, your uniqueness, what you stand for.", a: "Christine Comaford-Lynch", r: "Business Strategist" },
  { q: "Done is better than perfect.", a: "Sheryl Sandberg", r: "Author, Lean In" },
  { q: "Make a customer, not a sale.", a: "Katherine Barchetti", r: "Retail Pioneer" },
  { q: "Surround yourself with only people who are going to lift you higher.", a: "Oprah Winfrey", r: "Media Executive" },
  { q: "The question isn't who's going to let me; it's who's going to stop me.", a: "Ayn Rand", r: "Author & Philosopher" },
  { q: "Your most unhappy customers are your greatest source of learning.", a: "Bill Gates", r: "Co-founder, Microsoft" },
  { q: "If opportunity doesn't knock, build a door.", a: "Milton Berle", r: "Entrepreneur & Performer" },
  { q: "You don't build a business — you build people — and then people build the business.", a: "Zig Ziglar", r: "Sales Trainer & Author" },
  { q: "The best way to predict the future is to create it.", a: "Peter Drucker", r: "Management Consultant" },
  { q: "Networking is about connecting people with people, ideas and opportunities.", a: "Michele Jennae", r: "Networking Author" },
  { q: "There are no traffic jams along the extra mile.", a: "Roger Staubach", r: "Entrepreneur" },
  { q: "Success is most often achieved by those who don't know that failure is inevitable.", a: "Coco Chanel", r: "Founder, Chanel" },
  { q: "The key to successful leadership today is influence, not authority.", a: "Ken Blanchard", r: "Leadership Author" },
  { q: "Don't let anyone rob you of your imagination, your creativity, or your curiosity.", a: "Mae Jemison", r: "NASA Astronaut & Founder" },
  { q: "If you're offered a seat on a rocket ship, don't ask what seat. Just get on.", a: "Sheryl Sandberg", r: "Tech Executive" },
  { q: "I attribute my success to this: I never gave or took any excuse.", a: "Florence Nightingale", r: "Nursing Pioneer" },
  { q: "When you can't find someone to follow, you have to find a way to lead by example.", a: "Roxanne Quimby", r: "Co-founder, Burt's Bees" },
  { q: "Business opportunities are like buses — there's always another one coming.", a: "Richard Branson", r: "Founder, Virgin Group" },
  { q: "The most courageous act is still to think for yourself. Aloud.", a: "Coco Chanel", r: "Fashion Pioneer" },
  { q: "Quality is more important than quantity. One home run is much better than two doubles.", a: "Steve Jobs", r: "Co-founder, Apple" },
  { q: "If you're not willing to risk the usual, you will have to settle for the ordinary.", a: "Jim Rohn", r: "Entrepreneur & Author" },
  { q: "The way to get started is to quit talking and begin doing.", a: "Walt Disney", r: "Founder, The Walt Disney Company" },
  { q: "You miss 100% of the shots you don't take.", a: "Wayne Gretzky", r: "Athlete & Entrepreneur" },
  { q: "Whatever the mind of man can conceive and believe, it can achieve.", a: "Napoleon Hill", r: "Author, Think and Grow Rich" },
  { q: "Great things in business are never done by one person; they're done by a team of people.", a: "Steve Jobs", r: "Visionary" },
  { q: "If we have data, let's look at data. If all we have are opinions, let's go with mine.", a: "Jim Barksdale", r: "Former CEO, Netscape" },
  { q: "I have learned that when one's mind is made up, this diminishes fear.", a: "Rosa Parks", r: "Activist & Leader" },
  { q: "A business that makes nothing but money is a poor business.", a: "Henry Ford", r: "Founder, Ford Motor Co." },
  { q: "The customer's perception is your reality.", a: "Kate Zabriskie", r: "Customer Service Author" },
  { q: "Hire character. Train skill.", a: "Peter Schutz", r: "Former CEO, Porsche" },
  { q: "You cannot mandate productivity; you must provide the tools to let people become their best.", a: "Steve Jobs", r: "Apple" },
  { q: "The biggest risk is not taking any risk.", a: "Mark Zuckerberg", r: "Founder, Meta" },
  { q: "Don't try to be the next someone else. Be the first you.", a: "Indra Nooyi", r: "Former CEO, PepsiCo" },
  { q: "I believe in being strong when everything seems to be going wrong.", a: "Audrey Hepburn", r: "Actor & Humanitarian" },
  { q: "Power is not given to you. You have to take it.", a: "Beyoncé", r: "Entrepreneur & Performer" },
];


